/**
 * Toast system — Wave 17 (design-system).
 *
 * Substitui `alert()` por notificações não-bloqueantes, acessíveis (radix),
 * com variantes semânticas. Uso:
 *
 *   const { toast } = useToast();
 *   toast.success("Salvo!");
 *   toast.error("Falha ao salvar", "detalhe opcional");
 *   toast({ title: "...", description: "...", variant: "info", durationMs: 6000 });
 */
import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";

type Variant = "success" | "error" | "warning" | "info";

type ToastItem = {
  id: number;
  title: string;
  description?: string;
  variant: Variant;
  durationMs: number;
};

type ToastInput = { title: string; description?: string; variant?: Variant; durationMs?: number };

type ToastFn = ((input: ToastInput) => void) & {
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
};

const ToastContext = createContext<{ toast: ToastFn } | null>(null);

const VARIANT_META: Record<Variant, { icon: typeof Info; cls: string; iconCls: string }> = {
  success: { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50", iconCls: "text-emerald-600" },
  error: { icon: XCircle, cls: "border-red-200 bg-red-50", iconCls: "text-red-600" },
  warning: { icon: AlertTriangle, cls: "border-amber-200 bg-amber-50", iconCls: "text-amber-600" },
  info: { icon: Info, cls: "border-blue-200 bg-blue-50", iconCls: "text-blue-600" },
};

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useMemo(() => {
    const base = (input: ToastInput) => {
      const item: ToastItem = {
        id: ++counter,
        title: input.title,
        description: input.description,
        variant: input.variant ?? "info",
        durationMs: input.durationMs ?? (input.variant === "error" ? 7000 : 4500),
      };
      setItems((prev) => [...prev, item]);
    };
    const fn = base as ToastFn;
    fn.success = (title, description) => base({ title, description, variant: "success" });
    fn.error = (title, description) => base({ title, description, variant: "error" });
    fn.warning = (title, description) => base({ title, description, variant: "warning" });
    fn.info = (title, description) => base({ title, description, variant: "info" });
    return fn;
  }, []);

  const ctx = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={ctx}>
      <ToastPrimitive.Provider swipeDirection="right" duration={4500}>
        {children}
        {items.map((t) => {
          const meta = VARIANT_META[t.variant];
          const Icon = meta.icon;
          return (
            <ToastPrimitive.Root
              key={t.id}
              duration={t.durationMs}
              onOpenChange={(open) => { if (!open) dismiss(t.id); }}
              className={`group pointer-events-auto relative flex w-full items-start gap-3 rounded-md border px-4 py-3 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-right-full ${meta.cls}`}
            >
              <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.iconCls}`} />
              <div className="flex-1 min-w-0">
                <ToastPrimitive.Title className="text-sm font-semibold text-foreground">
                  {t.title}
                </ToastPrimitive.Title>
                {t.description && (
                  <ToastPrimitive.Description className="text-xs text-muted-foreground mt-0.5 break-words">
                    {t.description}
                  </ToastPrimitive.Description>
                )}
              </div>
              <ToastPrimitive.Close
                className="shrink-0 rounded p-0.5 text-muted-foreground/70 hover:text-foreground hover:bg-black/5"
                aria-label="Fechar"
              >
                <X className="h-3.5 w-3.5" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[120] flex max-h-screen w-full flex-col gap-2 p-4 sm:max-w-sm" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): { toast: ToastFn } {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback defensivo: se algum componente usar fora do provider, não quebra.
    const noop = ((_input: ToastInput) => { /* noop */ }) as ToastFn;
    noop.success = () => {}; noop.error = () => {}; noop.warning = () => {}; noop.info = () => {};
    return { toast: noop };
  }
  return ctx;
}
